import _ from 'lodash';
import dayjs from 'dayjs';
import { Form, Spin } from 'antd';
import { useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { formatNumber } from '../utils/helperFunc';
import SectionCard from 'components/section/SectionCard';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import LightTable from 'static-solutions/24155/identity-solution/lease-v9/lease-record-management-v9/CustomComponents/LeaseUtils/LightTable';
import { fontSize } from '@mui/system';

const HeadFilterChip = ({ head, isActive, onChipClick }) => {
	const chipStyles = {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 0.75,
		px: 2,
		py: 1,
		borderRadius: '5px',
		cursor: 'pointer',
		transition: 'all 0.25s ease-in-out',
		border: '1px solid #1A1D2E',
		minWidth: '120px',
		justifyContent: 'center',

		...(isActive
			? {
					backgroundColor: '#1A1D2E',
					color: '#fff',
				}
			: {
					border: '1px solid #D1D5DC',
					backgroundColor: '#FFFFFF',
					color: '#364153',
					opacity: 1,
					'&:hover': {
						backgroundColor: '#F9FAFB',
					},
				}),

		'& .MuiTypography-root': {
			fontSize: '12px !important',
		},
	};

	return (
		<Box sx={chipStyles} onClick={() => onChipClick(head)}>
			<Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1, color: 'inherit' }}>
				{head}
			</Typography>
		</Box>
	);
};

const LeaseManagementV9GeneralLedgersDetail = forwardRef((_props, ref) => {
	const form = Form.useFormInstance();
	const { state } = useLocation();
	const [sendControlDataForShowAll, { data: generalLedgerData, isLoading: isSendControlDataLoading }] = useSendControlDataMutation();
	const previousPayloadRef = useRef(null);
	const [selectedHeads, setSelectedHeads] = useState(['All']);

	const loadData = useCallback(async () => {
		const payloadData = {
			leaseId: form.getFieldValue('leaseId'),
		};

		if (
			previousPayloadRef.current &&
			previousPayloadRef.current.leaseId === payloadData.leaseId &&
			previousPayloadRef.current.processCode === state?.processCode
		) {
			return;
		}

		const convertJson = JSON.stringify(payloadData);
		const convertBase64 = btoa(convertJson);

		const formattedPayload = {
			data: convertBase64,
			processCode: state?.processCode,
			scriptName: 'lease-controller-schedule-get-findata',
			payloadData: payloadData,
		};

		previousPayloadRef.current = {
			...payloadData,
			processCode: state?.processCode,
		};

		await sendControlDataForShowAll(formattedPayload);
	}, [form, sendControlDataForShowAll, state?.processCode]);

	useImperativeHandle(
		ref,
		() => ({
			loadData,
		}),
		[loadData]
	);
	// useEffect(() => {
	// 	loadData();
	// }, []);

	const columnData = [
		{
			id: 'recordDocId',
			width: '5%',
			align: 'center',
			accessor: (data) => data?.latestProcessGroup?.recordDocId,
			cellAlign: 'center',
			Header: 'Document No',
			cellSx: { fontSize: '13px' },
		},
		{
			id: 'recordInputDate',
			width: '5%',
			align: 'center',
			accessor: (data) => {
				return dayjs(data?.latestProcessGroup?.recordInputDate?.dateString).format('YYYY-MM-DD');
			},
			cellAlign: 'center',
			Header: 'Document Date',
			cellSx: { fontSize: '13px' },
		},

		{
			id: 'referenceId',
			width: '5%',
			align: 'center',
			accessor: (data) => data?.latestProcessGroup?.serialNumber,
			cellAlign: 'center',
			Header: 'Reference Id',
			cellSx: { fontSize: '13px' },
		},
		{
			id: 'recordCreationDateTime',
			width: '5%',
			align: 'center',
			accessor: (data) => {
				return dayjs(data?.latestProcessGroup?.creationDateTime?.dateString).format('YYYY-MM-DD');
			},
			cellAlign: 'center',
			Header: 'Reference Date',
			cellSx: { fontSize: '13px' },
		},
		{
			id: 'description',
			width: '5%',
			align: 'left',
			accessor: (data) => data?.description,
			cellAlign: 'left',
			Header: 'Description',
			cellSx: { fontSize: '13px' },
		},
		{
			id: 'processStatus',
			width: '5%',
			align: 'center',
			accessor: (data) => data?.latestProcessGroup?.processStatus,
			cellAlign: 'center',
			Header: 'Type',
			cellSx: { fontSize: '13px' },
		},

		{
			id: 'total',
			width: '5%',
			align: 'right',
			accessor: 'total',
			cellAlign: 'center',
			Header: 'Amount',
			Cell: (cell) => {
				const debit = cell?.row?.original?.debit ? parseFloat(cell?.row?.original?.debit) : 0;
				const credit = cell?.row?.original?.credit ? parseFloat(cell?.row?.original?.credit) : 0;
				const total = isNaN(debit) ? 0 : debit - (isNaN(credit) ? 0 : credit);

				return (
					<Typography align="right" sx={{ fontSize: '13px !important' }}>
						{total !== 0 ? (total > 0 ? formatNumber(total, 2) : '(' + formatNumber(Math.abs(total), 2) + ')') : ''}
					</Typography>
				);
			},
		},

		{
			id: 'comulativeAmount',
			width: '5%',
			align: 'right',
			accessor: 'comulativeAmount',
			cellAlign: 'right',
			Header: 'Cumulative Amount',
			cellSx: { fontSize: '13px' },
		},
	];

	const groupDataByHead = (data) => {
		try {
			if (!Array.isArray(data) || data.length === 0) {
				console.warn('groupDataByHead: Invalid or empty data provided');
				return [];
			}

			// Filter out items with null or undefined headCode
			const filteredData = data.filter((item) => {
				return item && typeof item === 'object' && item?.headCode != null && item?.headCode !== undefined;
			});

			if (filteredData.length === 0) {
				console.warn('groupDataByHead: No valid items with headCode found');
				return [];
			}

			// First, group the data by the 'headCode' field
			const grouped = _.groupBy(filteredData, 'headCode');

			if (!grouped || Object.keys(grouped).length === 0) {
				console.warn('groupDataByHead: No grouped data found');
				return [];
			}

			// Create a sorted result based on headCode (as integers)
			const result = Object.keys(grouped)
				.map((headCode) => {
					const parsedHeadCode = parseInt(headCode, 10);
					return isNaN(parsedHeadCode) ? null : parsedHeadCode;
				})
				.filter((headCode) => headCode !== null) // Remove invalid headCodes
				.sort((a, b) => a - b) // Sort numerically
				.map((headCode) => {
					const values = grouped[headCode];

					if (!Array.isArray(values) || values.length === 0) {
						console.warn(`groupDataByHead: No values found for headCode ${headCode}`);
						return null;
					}

					let runningTotal = 0;

					// Calculate running totals and format data
					const indexedValues = values
						.map((item, index) => {
							if (!item || typeof item !== 'object') {
								console.warn(`groupDataByHead: Invalid item at index ${index} for headCode ${headCode}`);
								return null;
							}

							const { ...rest } = item;

							// Calculate the value for this row (debit - credit)
							const debit = parseFloat(item.debit || 0);
							const credit = parseFloat(item.credit || 0);
							const rowValue = (isNaN(debit) ? 0 : debit) - (isNaN(credit) ? 0 : credit);

							// Update running total
							runningTotal += rowValue;

							// Format the cumulative amount
							const formattedCumulative =
								runningTotal !== 0
									? runningTotal > 0
										? formatNumber(runningTotal, 2)
										: '(' + formatNumber(Math.abs(runningTotal), 2) + ')'
									: '';

							return {
								...rest,
								index,
								comulativeAmount: formattedCumulative,
							};
						})
						.filter((item) => item !== null); // Remove null items

					// Get head name safely
					const headName = values && values.length > 0 && values[0] && values[0].head ? values[0].head : `Head ${headCode}`;

					return {
						headCode,
						head: headName,
						value: indexedValues || [],
					};
				})
				.filter((group) => group !== null); // Remove null groups

			return result || [];
		} catch (error) {
			console.error('Error in groupDataByHead:', error);
			return [];
		}
	};

	const filteredData = useMemo(() => {
		try {
			if (!Array.isArray(generalLedgerData?.responseData)) {
				return [];
			}

			return generalLedgerData.responseData.filter((item) => {
				return item && typeof item === 'object' && item?.cancelStatus?.toLowerCase() !== 'cancelled';
			});
		} catch (error) {
			console.error('Error filtering data:', error);
			return [];
		}
	}, [generalLedgerData?.responseData]);

	const groupedData = useMemo(() => groupDataByHead(filteredData), [filteredData]);

	const uniqueHeads = useMemo(() => groupedData.map((group) => `${group.value?.[0]?.headGroup || 'Unknown'} [${group.headCode}]`), [groupedData]);

	const dataToRender = useMemo(() => {
		if (selectedHeads.includes('All')) {
			return groupedData;
		}

		return groupedData.filter((group) => selectedHeads.includes(`${group.value?.[0]?.headGroup || 'Unknown'} [${group.headCode}]`));
	}, [groupedData, selectedHeads]);

	useEffect(() => {
		setSelectedHeads(['All']);
	}, [uniqueHeads]);

	const handleFilterChange = useCallback((clickedHead) => {
		if (clickedHead === 'All') {
			setSelectedHeads(['All']);
			return;
		}

		setSelectedHeads((prev) => {
			const currentIndividualFilters = prev.filter((h) => h !== 'All');

			let newSelection;

			if (currentIndividualFilters.includes(clickedHead)) {
				newSelection = currentIndividualFilters.filter((h) => h !== clickedHead);
			} else {
				newSelection = [...currentIndividualFilters, clickedHead];
			}

			if (newSelection.length === 0) {
				return ['All'];
			}

			return newSelection;
		});
	}, []);

	return (
		<Box>
			<Spin spinning={isSendControlDataLoading} style={{ maxHeight: '100%' }}>
				<SectionCard
					sx={{
						padding: '16px',
						borderRadius: '10px',
						border: '1px solid #e6ebf2',
					}}
				>
					{uniqueHeads.length > 1 && (
						<Box
							sx={{
								mb: 2,
								gap: 1.5,
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'flex-start',
								pb: '16px',
							}}
						>
							<Typography variant="subtitle1" sx={{ fontSize: '18px', fontWeight: 500, color: '#364153' }}>
								Filter by Ledger:
							</Typography>

							<Box
								sx={{
									gap: 1.5,
									display: 'flex',
									flexWrap: 'wrap',
									alignItems: 'center',
								}}
							>
								<HeadFilterChip
									key="all-filter"
									head="All"
									isActive={selectedHeads.includes('All')}
									onChipClick={handleFilterChange}
								/>

								{uniqueHeads.map((head) => (
									<HeadFilterChip key={head} head={head} isActive={selectedHeads.includes(head)} onChipClick={handleFilterChange} />
								))}
							</Box>
						</Box>
					)}

					{groupedData.length > 0 ? (
						<div className="lease-details-tables">
							{dataToRender.map((group) => {
								const itemIdentifier = `${group.value?.[0]?.headGroup || 'Unknown'} [${group.headCode}]`;

								return (
									<Box key={itemIdentifier}>
										<Typography
											variant="h4"
											sx={{
												fontWeight: 600,
												fontSize: '18px',
												color: '#1E3A5F',
												marginBottom: '16px',
												padding: '0 0 12px 0',
												borderBottom: '1px solid #e0e6ed',
											}}
										>
											{itemIdentifier}
										</Typography>

										<div className="head-group-table" style={{ marginBottom: '30px' }}>
											<LightTable
												columns={columnData}
												emptyMessage="No data available for this head"
												data={Array.isArray(group.value) ? group.value : []}
											/>
										</div>
									</Box>
								);
							})}

							{dataToRender.length === 0 && !selectedHeads.includes('All') && (
								<Box
									sx={{
										p: 4,
										display: 'flex',
										minHeight: '150px',
										textAlign: 'center',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									<Typography>No ledgers match the selected filters.</Typography>
								</Box>
							)}
						</div>
					) : (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
							<Typography>{isSendControlDataLoading ? 'Loading...' : 'No Ledger Available Right Now'}</Typography>
						</Box>
					)}
				</SectionCard>
			</Spin>
		</Box>
	);
});

LeaseManagementV9GeneralLedgersDetail.displayName = 'LeaseDetailsShowAll';

export default LeaseManagementV9GeneralLedgersDetail;

// import _ from 'lodash';
// import dayjs from 'dayjs';
// import { Form, Spin } from 'antd';
// import { useLocation } from 'react-router-dom';
// import { Box, Typography } from '@mui/material';
// import { formatNumber } from '../utils/helperFunc';
// import { AnimatePresence, motion } from 'framer-motion';
// import { useSendControlDataMutation } from 'store/features/api/apiSlice';
// import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
// import LightTable from 'static-solutions/24155/identity-solution/lease-v9/lease-record-management-v9/CustomComponents/LeaseUtils/LightTable';

// const HeadFilterChip = ({ head, isActive, onChipClick }) => {
// 	const chipStyles = {
// 		display: 'inline-flex',
// 		alignItems: 'center',
// 		gap: 0.75,
// 		px: 2,
// 		py: 1,
// 		borderRadius: '5px',
// 		cursor: 'pointer',
// 		transition: 'all 0.25s ease-in-out',
// 		border: '1px solid #005aa3', // Consistent color
// 		minWidth: '120px',
// 		justifyContent: 'center',
// 		...(isActive
// 			? {
// 					backgroundColor: '#005aa3', // Solid color when active
// 					color: '#fff',
// 					boxShadow: '0 4px 15px -5px rgba(0, 90, 163, 0.4)',
// 					transform: 'translateY(-2px)',
// 				}
// 			: {
// 					backgroundColor: 'transparent',
// 					color: '#005aa3', // Colored text when inactive
// 					opacity: 0.9,
// 					'&:hover': {
// 						backgroundColor: 'rgba(0, 90, 163, 0.08)',
// 					},
// 				}),
// 	};

// 	return (
// 		<Box sx={chipStyles} onClick={() => onChipClick(head)}>
// 			<Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1, color: 'inherit' }}>
// 				{head}
// 			</Typography>
// 		</Box>
// 	);
// };

// const LeaseManagementV9GeneralLedgersDetail = forwardRef((_props, ref) => {
// 	const form = Form.useFormInstance();
// 	const { state } = useLocation();
// 	const [sendControlDataForShowAll, { data: generalLedgerData, isLoading: isSendControlDataLoading }] = useSendControlDataMutation();
// 	const [previousPayload, setPreviousPayload] = useState(null);
// 	const [selectedHeads, setSelectedHeads] = useState(['All']);

// 	const loadData = async () => {
// 		const payloadData = {
// 			leaseId: form.getFieldValue('leaseId'),
// 		};

// 		// Check if the payload is the same as the previous call
// 		if (previousPayload && previousPayload.leaseId === payloadData.leaseId && previousPayload.processCode === state?.processCode) {
// 			// console.log('Skipping API call - same inputs as previous call');
// 			return;
// 		}

// 		const convertJson = JSON.stringify(payloadData);
// 		const convertBase64 = btoa(convertJson);

// 		const formattedPayload = {
// 			data: convertBase64,
// 			processCode: state?.processCode,
// 			scriptName: 'lease-controller-schedule-get-findata',
// 			payloadData: payloadData,
// 		};

// 		// Store the current payload for future comparison
// 		setPreviousPayload({
// 			...payloadData,
// 			processCode: state?.processCode,
// 		});

// 		await sendControlDataForShowAll(formattedPayload);
// 	};

// 	useImperativeHandle(ref, () => ({
// 		loadData,
// 	}));
// 	// useEffect(() => {
// 	// 	loadData();
// 	// }, []);

// 	const columnData = [
// 		{
// 			id: 'recordDocId',
// 			width: '5%',
// 			align: 'center',
// 			accessor: (data) => data?.latestProcessGroup?.recordDocId,
// 			cellAlign: 'center',
// 			Header: 'Document No',
// 			cellSx: { fontSize: '13px' },
// 		},
// 		{
// 			id: 'recordInputDate',
// 			width: '5%',
// 			align: 'center',
// 			accessor: (data) => {
// 				return dayjs(data?.latestProcessGroup?.recordInputDate?.dateString).format('YYYY-MM-DD');
// 			},
// 			cellAlign: 'center',
// 			Header: 'Document Date',
// 			cellSx: { fontSize: '13px' },
// 		},

// 		{
// 			id: 'referenceId',
// 			width: '5%',
// 			align: 'center',
// 			accessor: (data) => data?.latestProcessGroup?.serialNumber,
// 			cellAlign: 'center',
// 			Header: 'Reference Id',
// 			cellSx: { fontSize: '13px' },
// 		},
// 		{
// 			id: 'recordCreationDateTime',
// 			width: '5%',
// 			align: 'center',
// 			accessor: (data) => {
// 				return dayjs(data?.latestProcessGroup?.creationDateTime?.dateString).format('YYYY-MM-DD');
// 			},
// 			cellAlign: 'center',
// 			Header: 'Reference Date',
// 			cellSx: { fontSize: '13px' },
// 		},
// 		{
// 			id: 'description',
// 			width: '5%',
// 			align: 'left',
// 			accessor: (data) => data?.description,
// 			cellAlign: 'left',
// 			Header: 'Description',
// 			cellSx: { fontSize: '13px' },
// 		},
// 		{
// 			id: 'processStatus',
// 			width: '5%',
// 			align: 'center',
// 			accessor: (data) => data?.latestProcessGroup?.processStatus,
// 			cellAlign: 'center',
// 			Header: 'Type',
// 			cellSx: { fontSize: '13px' },
// 		},

// 		{
// 			id: 'total',
// 			width: '5%',
// 			align: 'right',
// 			accessor: 'total',
// 			cellAlign: 'center',
// 			Header: 'Amount',
// 			Cell: (cell) => {
// 				const debit = cell?.row?.original?.debit ? parseFloat(cell?.row?.original?.debit) : 0;
// 				const credit = cell?.row?.original?.credit ? parseFloat(cell?.row?.original?.credit) : 0;
// 				const total = isNaN(debit) ? 0 : debit - (isNaN(credit) ? 0 : credit);

// 				return (
// 					<Typography align="right" sx={{ fontSize: '13px !important' }}>
// 						{total !== 0 ? (total > 0 ? formatNumber(total, 2) : '(' + formatNumber(Math.abs(total), 2) + ')') : ''}
// 					</Typography>
// 				);
// 			},
// 		},

// 		{
// 			id: 'comulativeAmount',
// 			width: '5%',
// 			align: 'right',
// 			accessor: 'comulativeAmount',
// 			cellAlign: 'right',
// 			Header: 'Cumulative Amount',
// 			cellSx: { fontSize: '13px' },
// 		},
// 	];

// 	const groupDataByHead = (data) => {
// 		try {
// 			if (!Array.isArray(data) || data.length === 0) {
// 				console.warn('groupDataByHead: Invalid or empty data provided');
// 				return [];
// 			}

// 			// Filter out items with null or undefined headCode
// 			const filteredData = data.filter((item) => {
// 				return item && typeof item === 'object' && item?.headCode != null && item?.headCode !== undefined;
// 			});

// 			if (filteredData.length === 0) {
// 				console.warn('groupDataByHead: No valid items with headCode found');
// 				return [];
// 			}

// 			// First, group the data by the 'headCode' field
// 			const grouped = _.groupBy(filteredData, 'headCode');

// 			if (!grouped || Object.keys(grouped).length === 0) {
// 				console.warn('groupDataByHead: No grouped data found');
// 				return [];
// 			}

// 			// Create a sorted result based on headCode (as integers)
// 			const result = Object.keys(grouped)
// 				.map((headCode) => {
// 					const parsedHeadCode = parseInt(headCode, 10);
// 					return isNaN(parsedHeadCode) ? null : parsedHeadCode;
// 				})
// 				.filter((headCode) => headCode !== null) // Remove invalid headCodes
// 				.sort((a, b) => a - b) // Sort numerically
// 				.map((headCode) => {
// 					const values = grouped[headCode];

// 					if (!Array.isArray(values) || values.length === 0) {
// 						console.warn(`groupDataByHead: No values found for headCode ${headCode}`);
// 						return null;
// 					}

// 					let runningTotal = 0;

// 					// Calculate running totals and format data
// 					const indexedValues = values
// 						.map((item, index) => {
// 							if (!item || typeof item !== 'object') {
// 								console.warn(`groupDataByHead: Invalid item at index ${index} for headCode ${headCode}`);
// 								return null;
// 							}

// 							const { ...rest } = item;

// 							// Calculate the value for this row (debit - credit)
// 							const debit = parseFloat(item.debit || 0);
// 							const credit = parseFloat(item.credit || 0);
// 							const rowValue = (isNaN(debit) ? 0 : debit) - (isNaN(credit) ? 0 : credit);

// 							// Update running total
// 							runningTotal += rowValue;

// 							// Format the cumulative amount
// 							const formattedCumulative =
// 								runningTotal !== 0
// 									? runningTotal > 0
// 										? formatNumber(runningTotal, 2)
// 										: '(' + formatNumber(Math.abs(runningTotal), 2) + ')'
// 									: '';

// 							return {
// 								...rest,
// 								index,
// 								comulativeAmount: formattedCumulative,
// 							};
// 						})
// 						.filter((item) => item !== null); // Remove null items

// 					// Get head name safely
// 					const headName = values && values.length > 0 && values[0] && values[0].head ? values[0].head : `Head ${headCode}`;

// 					return {
// 						headCode,
// 						head: headName,
// 						value: indexedValues || [],
// 					};
// 				})
// 				.filter((group) => group !== null); // Remove null groups

// 			return result || [];
// 		} catch (error) {
// 			console.error('Error in groupDataByHead:', error);
// 			return [];
// 		}
// 	};

// 	const filteredData = (() => {
// 		try {
// 			if (!Array.isArray(generalLedgerData?.responseData)) {
// 				return [];
// 			}

// 			return generalLedgerData.responseData.filter((item) => {
// 				return item && typeof item === 'object' && item?.cancelStatus?.toLowerCase() !== 'cancelled';
// 			});
// 		} catch (error) {
// 			console.error('Error filtering data:', error);
// 			return [];
// 		}
// 	})();

// 	const groupedData = groupDataByHead(filteredData);

// 	// REMOVED useMemo
// 	const uniqueHeads = groupedData.map((group) => `${group.value?.[0]?.headGroup || 'Unknown'} [${group.headCode}]`);

// 	const dataToRender = (() => {
// 		if (selectedHeads.includes('All')) {
// 			return groupedData;
// 		}

// 		return groupedData.filter((group) => selectedHeads.includes(`${group.value?.[0]?.headGroup || 'Unknown'} [${group.headCode}]`));
// 	})();

// 	// --- End of Derived Data Chain ---

// 	useEffect(() => {
// 		setSelectedHeads(['All']);
// 	}, [JSON.stringify(uniqueHeads)]);

// 	const handleFilterChange = (clickedHead) => {
// 		if (clickedHead === 'All') {
// 			// If "All" is clicked, it becomes the only selection.
// 			setSelectedHeads(['All']);
// 			return;
// 		}

// 		// If an individual item is clicked:
// 		setSelectedHeads((prev) => {
// 			// Start with the previous selections, but remove 'All' if it's there.
// 			const currentIndividualFilters = prev.filter((h) => h !== 'All');

// 			let newSelection;

// 			if (currentIndividualFilters.includes(clickedHead)) {
// 				// If already selected, de-select it.
// 				newSelection = currentIndividualFilters.filter((h) => h !== clickedHead);
// 			} else {
// 				// If not selected, add it to the list.
// 				newSelection = [...currentIndividualFilters, clickedHead];
// 			}

// 			// If de-selecting the last item, default back to 'All'.
// 			if (newSelection.length === 0) {
// 				return ['All'];
// 			}

// 			return newSelection;
// 		});
// 	};

// 	return (
// 		<Box>
// 			<Spin spinning={isSendControlDataLoading} style={{ maxHeight: '100%' }}>
// 				{uniqueHeads.length > 1 && (
// 					<Box sx={{ p: 1, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
// 						<Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1, color: '#333' }}>
// 							Filter by Ledger:
// 						</Typography>

// 						<HeadFilterChip key="all-filter" head="All" isActive={selectedHeads.includes('All')} onChipClick={handleFilterChange} />

// 						{uniqueHeads.map((head) => (
// 							<HeadFilterChip key={head} head={head} isActive={selectedHeads.includes(head)} onChipClick={handleFilterChange} />
// 						))}
// 					</Box>
// 				)}

// 				{groupedData.length > 0 ? (
// 					<div className="lease-details-tables">
// 						<AnimatePresence>
// 							{/* Now this works because dataToRender is an array */}
// 							{dataToRender.map((group) => {
// 								const itemIdentifier = `${group.value?.[0]?.headGroup || 'Unknown'} [${group.headCode}]`;

// 								return (
// 									<motion.div
// 										layout="position"
// 										key={itemIdentifier}
// 										animate={{ opacity: 1, x: 0 }}
// 										initial={{ opacity: 0, x: 300 }}
// 										exit={{ opacity: 0, x: 300, transition: { duration: 0.3 } }}
// 										transition={{ type: 'spring', stiffness: 250, damping: 30 }}
// 									>
// 										<Typography
// 											variant="h6"
// 											sx={{ fontSize: '16px !important', fontWeight: 500, color: '#000000', mb: '10px', pl: '11px' }}
// 										>
// 											{itemIdentifier}
// 										</Typography>

// 										<div className="head-group-table" style={{ marginBottom: '30px' }}>
// 											<LightTable
// 												columns={columnData}
// 												emptyMessage="No data available for this head"
// 												data={Array.isArray(group.value) ? group.value : []}
// 											/>
// 										</div>
// 									</motion.div>
// 								);
// 							})}
// 						</AnimatePresence>

// 						{dataToRender.length === 0 && !selectedHeads.includes('All') && (
// 							<Box
// 								sx={{
// 									p: 4,
// 									display: 'flex',
// 									minHeight: '150px',
// 									textAlign: 'center',
// 									alignItems: 'center',
// 									justifyContent: 'center',
// 								}}
// 							>
// 								<Typography>No ledgers match the selected filters.</Typography>
// 							</Box>
// 						)}
// 					</div>
// 				) : (
// 					<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
// 						<Typography>{isSendControlDataLoading ? 'Loading...' : 'No Ledger Available Right Now'}</Typography>
// 					</Box>
// 				)}
// 			</Spin>
// 		</Box>
// 	);
// });

// LeaseManagementV9GeneralLedgersDetail.displayName = 'LeaseDetailsShowAll';

// export default LeaseManagementV9GeneralLedgersDetail;
